import React, { useState, useMemo } from "react";
import { BedDouble, Sparkles, Info, Images } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice, useCurrency } from "@/contexts/CurrencyContext";
import { getRoomPricing, getServicePricing, formatPriceLine } from "@/lib/hotelPricing";
import { slugifyRoom } from "@/lib/roomRegistry";
import BoardOptionsDropdown from "@/components/BoardOptionsDropdown";
import ReserveThisHotelButton from "@/components/ReserveThisHotelButton";
import RoomPriceBlock from "@/components/pricing/RoomPriceBlock";

const API = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (url) =>
  !url ? null : url.startsWith("/api/") ? `${API}${url}` : url;

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const SECTION_COPY = {
  overline: {
    en: (cur) => `Rates · ${cur}`,
    fr: (cur) => `Tarifs · ${cur}`,
    es: (cur) => `Tarifas · ${cur}`,
  },
  title: { en: "Room & Service Details", fr: "Détails des chambres et services", es: "Detalles de habitaciones y servicios" },
  sub: {
    en: (cur) => `Public starting rates in ${cur}. Agency contracts and group rates are available on request.`,
    fr: (cur) => `Tarifs publics indicatifs en ${cur}. Tarifs agence et groupes disponibles sur demande.`,
    es: (cur) => `Tarifas públicas indicativas en ${cur}. Tarifas de agencia y grupos disponibles bajo petición.`,
  },
  tab_rooms: { en: "Rooms", fr: "Chambres", es: "Habitaciones" },
  tab_services: { en: "Services", fr: "Services", es: "Servicios" },
  public_price: { en: "Public rate", fr: "Tarif public", es: "Tarifa pública" },
  agency_price: { en: "Agency rate", fr: "Tarif agence", es: "Tarifa agencia" },
  on_request: { en: "On request", fr: "Sur demande", es: "Bajo petición" },
  disclaimer: {
    en: "Rates are indicative starting prices, excluding tourism tax and seasonal variations. Final pricing is confirmed at booking in MAD.",
    fr: "Tarifs indicatifs hors taxe de séjour et variations saisonnières. Le prix final est confirmé à la réservation en MAD.",
    es: "Tarifas indicativas, excluida la tasa turística y variaciones de temporada. El precio final se confirma en la reserva en MAD.",
  },
};

const PriceBlock = ({ pricing, room, lang, testid }) => {
  const line = formatPriceLine(pricing, lang);
  const ppp = pricing?.perPerson;
  const hasPricing = ppp?.fromMAD != null || (line && line.amount);
  return (
    <div className="mt-6 pt-5 border-t border-[#2C2621]/10 space-y-3" data-testid={testid}>
      {hasPricing ? (
        ppp?.fromMAD != null ? (
          <RoomPriceBlock
            occupancy={ppp.occupancy}
            amountMAD={ppp.fromMAD}
            boardBasis={ppp.cheapestKey || "room_only"}
            variant="detail"
            testid={`${testid}-block`}
          />
        ) : (
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
              {pick(SECTION_COPY.public_price, lang)}
            </span>
            <span className="text-right">
              <span className="font-serif-x text-2xl md:text-[28px] tracking-tight text-[#2C2621] leading-none">
                {line?.amount}
              </span>
              {line?.unit && (
                <span className="ml-2 text-xs text-[#5C5248] tracking-wide">{line.unit}</span>
              )}
            </span>
          </div>
        )
      ) : (
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
            {pick(SECTION_COPY.public_price, lang)}
          </span>
          <span className="text-sm text-[#5C5248] italic">{pick(SECTION_COPY.on_request, lang)}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]/80">
          {pick(SECTION_COPY.agency_price, lang)}
        </span>
        <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase px-2.5 py-1 border border-[#D4A373]/50 text-[#A07042] bg-[#D4A373]/10 rounded-full">
          {pick(SECTION_COPY.on_request, lang)}
        </span>
      </div>
    </div>
  );
};

// Board-plan options are rendered by the shared `<BoardOptionsDropdown>`
// component so the spotlight room cards and the editorial Room Rates strip
// on each hotel detail page stay visually + behaviourally in sync.

const RoomCard = ({ room, hotelSlug, lang, idx, adminGallery }) => {
  const pricing = getRoomPricing(room, hotelSlug);
  const meta = useMemo(() => {
    const surface = typeof room.surface === "string" ? room.surface : pick(room.surface, lang);
    return [surface, pick(room.capacity, lang)].filter(Boolean).join(" · ");
  }, [room.surface, room.capacity, lang]);
  // Cover precedence: gallery photo → CMS override / room.image
  const galleryImages = adminGallery?.images || [];
  const galleryCover = resolveUrl(galleryImages[0]?.url);
  const cover = galleryCover || resolveUrl(room.image);
  // Pull through CMS overrides surfaced by applyRoomOverrides
  const number = (room.number || "").trim();
  const type = (room.type || "").trim();
  const category = (room.category || "").trim();
  return (
    <article
      className="group bg-[#FDFBF7] border border-[#2C2621]/10 hover:border-[#D4A373]/60 transition-colors duration-300 flex flex-col overflow-hidden"
      data-testid={`pricing-room-card-${idx}`}
    >
      {cover && (
        <div className="relative aspect-[16/10] overflow-hidden bg-[#F2EBE1]" data-testid={`pricing-room-card-cover-${idx}`} data-no-cms-edit="true">
          <img
            src={cover}
            alt={pick(room.name, lang)}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
          />
          {galleryImages.length > 1 && (
            <span
              className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-[#1A1513]/80 backdrop-blur-sm text-[#FDFBF7] px-2.5 py-1.5 text-[9px] tracking-[0.22em] uppercase"
              data-testid={`pricing-room-card-count-${idx}`}
            >
              <Images className="w-3 h-3" strokeWidth={1.8} />
              {galleryImages.length}
            </span>
          )}
          {number && (
            <span
              className="absolute top-3 left-3 inline-flex items-center bg-[#FDFBF7]/90 backdrop-blur-sm text-[#2C2621] px-2 py-1 text-[9px] tracking-[0.22em] uppercase"
              data-testid={`pricing-room-card-number-${idx}`}
            >
              № {number}
            </span>
          )}
        </div>
      )}
      <div className="p-7 md:p-8 flex-1 flex flex-col">
        <div className="flex items-start gap-3 mb-4">
          <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 bg-[#F2EBE1] text-[#A07042] rounded-full shrink-0">
            <BedDouble className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif-x text-xl md:text-2xl text-[#2C2621] leading-tight tracking-tight">
              {pick(room.name, lang)}
            </h3>
            {meta && (
              <p className="mt-1 text-[11px] tracking-[0.2em] uppercase text-[#5C5248]/85">
                {meta}
              </p>
            )}
            {(type || category || (!cover && number)) && (
              <p
                className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase"
                data-testid={`pricing-room-card-classification-${idx}`}
              >
                {!cover && number && (
                  <span className="bg-[#2C2621] text-[#FDFBF7] px-2 py-0.5">№ {number}</span>
                )}
                {type && (
                  <span className="text-[#A07042]">{type}</span>
                )}
                {type && category && <span className="text-[#5C5248]/50">·</span>}
                {category && (
                  <span className="text-[#5C5248]">{category}</span>
                )}
              </p>
            )}
          </div>
        </div>
        <p className="text-sm text-[#5C5248] leading-relaxed line-clamp-4">
          {pick(room.desc, lang)}
        </p>
        <PriceBlock pricing={pricing} room={room} lang={lang} testid={`pricing-room-rate-${idx}`} />
        {pricing?.plans && (
          <div className="mt-4">
            <BoardOptionsDropdown
              plans={pricing.perPerson?.plans || pricing.plans}
              occupancy={pricing.perPerson?.occupancy}
              lang={lang}
              testid={`pricing-room-board-${idx}`}
            />
          </div>
        )}
        <div className="mt-5 pt-5 border-t border-[#2C2621]/10">
          <ReserveThisHotelButton
            hotelSlug={hotelSlug}
            testid={`pricing-room-reserve-${idx}`}
            className="w-full"
          />
        </div>
      </div>
    </article>
  );
};

const ServiceCard = ({ service, hotelSlug, lang, idx }) => {
  const pricing = getServicePricing(service, hotelSlug);
  return (
    <article
      className="group bg-[#FDFBF7] border border-[#2C2621]/10 hover:border-[#D4A373]/60 transition-colors duration-300 flex flex-col"
      data-testid={`pricing-service-card-${idx}`}
    >
      <div className="p-7 md:p-8 flex-1 flex flex-col">
        <div className="flex items-start gap-3 mb-4">
          <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 bg-[#F2EBE1] text-[#A07042] rounded-full shrink-0">
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif-x text-xl md:text-2xl text-[#2C2621] leading-tight tracking-tight">
              {pick(service.title, lang)}
            </h3>
          </div>
        </div>
        <p className="text-sm text-[#5C5248] leading-relaxed line-clamp-4">
          {pick(service.desc, lang)}
        </p>
        <PriceBlock pricing={pricing} lang={lang} testid={`pricing-service-rate-${idx}`} />
        <div className="mt-5 pt-5 border-t border-[#2C2621]/10">
          <ReserveThisHotelButton
            hotelSlug={hotelSlug}
            testid={`pricing-service-reserve-${idx}`}
            className="w-full"
          />
        </div>
      </div>
    </article>
  );
};

const RoomServicePricing = ({ rooms = [], services = [], hotelSlug, adminGalleries = {} }) => {
  const { lang } = useLanguage();
  const { currency } = useCurrency();
  const [tab, setTab] = useState("rooms");

  if (!rooms.length && !services.length) return null;

  const tabs = [
    { key: "rooms", label: pick(SECTION_COPY.tab_rooms, lang), count: rooms.length },
    { key: "services", label: pick(SECTION_COPY.tab_services, lang), count: services.length },
  ].filter((tb) => tb.count > 0);

  return (
    <section
      id="rooms-services"
      className="py-20 md:py-28 bg-[#F2EBE1]/30 border-y border-[#2C2621]/10 relative"
      data-testid="section-rooms-services-pricing"
    >
      <div className="absolute inset-x-0 -top-px h-3 berber-bg-zigzag opacity-50 pointer-events-none" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-6">
            <p className="overline">{(SECTION_COPY.overline[lang] || SECTION_COPY.overline.en)(currency)}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#2C2621]">
              {pick(SECTION_COPY.title, lang)}
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-base md:text-lg leading-relaxed text-[#5C5248]">
              {(SECTION_COPY.sub[lang] || SECTION_COPY.sub.en)(currency)}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label={pick(SECTION_COPY.title, lang)}
          className="flex items-center gap-2 mb-10 border-b border-[#2C2621]/15"
          data-testid="pricing-tabs"
        >
          {tabs.map((tb) => {
            const active = tab === tb.key;
            return (
              <button
                key={tb.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(tb.key)}
                className={`relative px-5 py-3.5 text-[11px] tracking-[0.25em] uppercase transition-colors ${
                  active ? "text-[#2C2621]" : "text-[#5C5248]/70 hover:text-[#2C2621]"
                }`}
                data-testid={`pricing-tab-${tb.key}`}
              >
                {tb.label}
                <span className="ml-2 text-[#A07042] tabular-nums">
                  {String(tb.count).padStart(2, "0")}
                </span>
                {active && (
                  <span className="absolute -bottom-px left-0 right-0 h-[2px] bg-[#C16542]" />
                )}
              </button>
            );
          })}
        </div>

        {/* Cards */}
        {tab === "rooms" && rooms.length > 0 && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            data-testid="pricing-rooms-grid"
          >
            {rooms.map((room, i) => (
              <RoomCard
                key={`pricing-room-${pick(room.name, "en") || i}`}
                room={room}
                hotelSlug={hotelSlug}
                lang={lang}
                idx={i}
                adminGallery={adminGalleries[slugifyRoom(room.name)]}
              />
            ))}
          </div>
        )}

        {tab === "services" && services.length > 0 && (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            data-testid="pricing-services-grid"
          >
            {services.map((service, i) => (
              <ServiceCard
                key={`pricing-service-${pick(service.title, "en") || i}`}
                service={service}
                hotelSlug={hotelSlug}
                lang={lang}
                idx={i}
              />
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <div
          className="mt-12 flex items-start gap-3 max-w-3xl text-xs leading-relaxed text-[#5C5248]/80"
          data-testid="pricing-disclaimer"
        >
          <Info className="w-4 h-4 mt-0.5 text-[#A07042] shrink-0" strokeWidth={1.5} />
          <p>{pick(SECTION_COPY.disclaimer, lang)}</p>
        </div>
      </div>
    </section>
  );
};

export default RoomServicePricing;
